export class WebCryptoAesCbc {
  constructor() {
    this.algorithm = 'AES-CBC';
    this.keyLength = 256; // 256-bit key
    this.ivLength = 16; // 16 bytes (128 bits) for CBC
  }

  /**
   * Generate a random encryption key
   */
  async generateKey() {
    try {
      const key = await window.crypto.subtle.generateKey(
        {
          name: this.algorithm,
          length: this.keyLength,
        },
        true, // extractable
        ['encrypt', 'decrypt']
      );

      // Export key as raw bytes and convert to hex
      const exportedKey = await window.crypto.subtle.exportKey('raw', key);
      return this.arrayBufferToHex(exportedKey);
    } catch (error) {
      throw new Error(`Key generation failed: ${error.message}`);
    }
  }

  /**
   * Import a key from hex string
   */
  async importKey(keyHex) {
    try {
      const keyBuffer = this.hexToArrayBuffer(keyHex);
      
      return await window.crypto.subtle.importKey(
        'raw',
        keyBuffer,
        {
          name: this.algorithm,
          length: this.keyLength,
        },
        true, // extractable
        ['encrypt', 'decrypt']
      );
    } catch (error) {
      throw new Error(`Key import failed: ${error.message}`);
    }
  }

  /**
   * Generate random IV (16 bytes for AES-CBC)
   */
  generateIV() {
    const iv = new Uint8Array(this.ivLength);
    window.crypto.getRandomValues(iv);
    return this.arrayBufferToHex(iv.buffer);
  }

  /**
   * Encrypt data using AES-CBC
   */
  async encrypt(data, keyHex, ivHex = null) {
    try {
      const key = await this.importKey(keyHex);
      const iv = ivHex ? this.hexToArrayBuffer(ivHex) : window.crypto.getRandomValues(new Uint8Array(this.ivLength));
      
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(data);

      const encryptedBuffer = await window.crypto.subtle.encrypt(
        {
          name: this.algorithm,
          iv: iv,
        },
        key,
        dataBuffer
      );

      const actualIV = ivHex || this.arrayBufferToHex(iv);
      const ciphertext = this.arrayBufferToHex(encryptedBuffer);

      return {
        ciphertext: ciphertext,
        iv: actualIV,
        encryptedData: `${ciphertext}.${actualIV}`
      };
    } catch (error) {
      throw new Error(`Encryption failed: ${error.message}`);
    }
  }

  /**
   * Decrypt data using AES-CBC
   */
  async decrypt(encryptedData, keyHex) {
    try {
      // Parse the encrypted data format
      const [ciphertextHex, ivHex] = encryptedData.split('.');
      
      if (!ciphertextHex || !ivHex) {
        throw new Error('Invalid encrypted data format');
      }

      const key = await this.importKey(keyHex);
      const iv = this.hexToArrayBuffer(ivHex);
      const ciphertext = this.hexToArrayBuffer(ciphertextHex);

      const decryptedBuffer = await window.crypto.subtle.decrypt(
        {
          name: this.algorithm,
          iv: iv,
        },
        key,
        ciphertext
      );

      const decoder = new TextDecoder();
      return decoder.decode(decryptedBuffer);
    } catch (error) {
      console.error("Decryption error details:", error);
      if (error.name === 'OperationError') {
        throw new Error('Decryption failed - possible tampering, wrong key, or incorrect IV');
      }
      throw new Error(`Decryption failed: ${error.message}`);
    }
  }

  /**
   * Simple encryption with automatic IV generation that matches Java format
   * In Java AesCbcEncryptUtil, we prepend the IV to the ciphertext and then Base64 encode
   */
  async simpleEncrypt(data, keyHex) {
    const iv = window.crypto.getRandomValues(new Uint8Array(this.ivLength));
    const key = await this.importKey(keyHex);
    
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: this.algorithm,
        iv: iv,
      },
      key,
      dataBuffer
    );
    
    // Combine IV and ciphertext (IV first, then ciphertext)
    const combined = new Uint8Array(iv.byteLength + encryptedBuffer.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encryptedBuffer), iv.byteLength);
    
    // Base64 encode the result
    return this.arrayBufferToBase64(combined.buffer);
  }

  /**
   * Simple decryption that matches Java format
   * Expects a Base64 string where the first 16 bytes are the IV
   */
  async simpleDecrypt(encryptedBase64, keyHex) {
    try {
      // 1. Base64 decode
      const encryptedBytes = this.base64ToArrayBuffer(encryptedBase64);
      
      // 2. Extract IV (first 16 bytes) and ciphertext
      const iv = encryptedBytes.slice(0, this.ivLength);
      const ciphertext = encryptedBytes.slice(this.ivLength);
      
      const key = await this.importKey(keyHex);
      
      const decryptedBuffer = await window.crypto.subtle.decrypt(
        {
          name: this.algorithm,
          iv: new Uint8Array(iv),
        },
        key,
        ciphertext
      );
      
      const decoder = new TextDecoder();
      return decoder.decode(decryptedBuffer);
    } catch (error) {
      console.error("Simple decryption error details:", error);
      throw new Error(`Simple decryption failed: ${error.message}`);
    }
  }

  // Utility methods
  arrayBufferToHex(buffer) {
    const byteArray = new Uint8Array(buffer);
    return Array.from(byteArray, byte => {
      return ('0' + (byte & 0xFF).toString(16)).slice(-2);
    }).join('');
  }

  hexToArrayBuffer(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes.buffer;
  }

  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  base64ToArrayBuffer(base64) {
    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Check if Web Crypto API is available
   */
  isSupported() {
    return window.crypto && window.crypto.subtle;
  }
}

// Create singleton instance
export const webCrypto = new WebCryptoAesCbc();